// Bridge entry point. Picks the host-specific implementation:
//   - WKWebView (iOS) → native.ts
//   - WebView with AndroidBridge JS interface → android.ts
//   - Plain browser, production build → web.ts (real localStorage,
//     visibility events, git-sha-versioned X-Hearth-Client; native-only
//     methods return their "unavailable" shapes)
//   - Plain browser, dev / test → mock.ts (fakeable, side-effect-free)

import type { BridgeEvents, BridgeMethods } from './types'
import { createMockBridge } from './mock'
import { createNativeBridge } from './native'
import { createAndroidBridge } from './android'
import { createWebBridge } from './web'

export type Bridge = {
  call<K extends keyof BridgeMethods>(
    method: K,
    args?: BridgeMethods[K]['args'],
  ): Promise<BridgeMethods[K]['result']>
  on<K extends keyof BridgeEvents>(
    event: K,
    handler: (payload: BridgeEvents[K]) => void,
  ): () => void
  emit<K extends keyof BridgeEvents>(event: K, payload: BridgeEvents[K]): void
  readonly isNative: boolean
  readonly isAndroid: boolean
}

function detectIOS(): boolean {
  const w = window as unknown as {
    webkit?: { messageHandlers?: { hearth?: unknown } }
  }
  return Boolean(w.webkit?.messageHandlers?.hearth)
}

function detectAndroid(): boolean {
  return Boolean(
    (window as unknown as { AndroidBridge?: { postMessage?: unknown } })
      .AndroidBridge?.postMessage,
  )
}

// import.meta.env.PROD is true for `vite build` output (the deployed
// bundle) and false for `vite dev` + Vitest. So local web devs keep the
// fakeable mock while deployed browser sessions get the production web
// transport. Native shells short-circuit both regardless of PROD —
// the WKWebView / Android bridge detection wins.
const useWebBridge = Boolean(import.meta.env?.PROD)

export const bridge: Bridge = detectIOS()
  ? createNativeBridge()
  : detectAndroid()
    ? createAndroidBridge()
    : useWebBridge
      ? createWebBridge()
      : createMockBridge()

// Expose emit so Swift / Kotlin can push events into the React app.
// Native callers should use the buffering snippet below — on cold
// launch the WebView starts loading before React parses, so a direct
// `window.__bridge.emit(...)` would short-circuit and lose the event.
//
// Native pattern:
//   (function () {
//     if (window.__bridge) {
//       window.__bridge.emit('push.tapped', { ... });
//     } else {
//       (window.__pendingBridgeEvents = window.__pendingBridgeEvents || [])
//         .push(['push.tapped', { ... }]);
//     }
//   })();
//
// On bridge-module init (now), drain anything that arrived early.
;(window as unknown as { __bridge?: Bridge }).__bridge = bridge

type PendingEvent = [keyof BridgeEvents, BridgeEvents[keyof BridgeEvents]]
const w = window as unknown as { __pendingBridgeEvents?: PendingEvent[] }
const pending = w.__pendingBridgeEvents ?? []
delete w.__pendingBridgeEvents
// Defer to a microtask: api/ws.ts (and other modules that register
// bridge.on listeners) won't have run yet when this module is mid-eval.
// queueMicrotask waits until the synchronous top-level bundle code
// finishes, so the listeners exist before we replay buffered events.
queueMicrotask(() => {
  for (const [event, payload] of pending) bridge.emit(event, payload)
})

export type { BridgeEvents, BridgeMethods, Product } from './types'
